import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Table from "../../../../../components/table/table";
import {
  getCertificationTypesRequest,
  getInspectionServicesRequest,
  getInsuranceTypesRequest,
} from "../../../../../redux/inspection-firms/slice";
import TableHeaderTextField from "../../../../../components/table-header-text-filed/table-header-text-filed";
import TableHeaderSelectField from "../../../../../components/table-header-select-field/table-header-select-field";
import TableHeaderMultiSelectField from "../../../../../components/table-header-multi-select-field/table-header-multi-select-field";
import TableHeaderDatePicker from "../../../../../components/table-header-datepicker/table-header-datepicker";
import {
  getFormsRequest,
  getFormsSuccess,
} from "../../../../../redux/clients/slice";

dayjs.extend(utc);

const RestrictionsRenderer = ({ value }: any) => {
  const hasBackgroundCheck = value.hasOwnProperty("backgroundCheckDone");
  const hasCertifications =
    value?.certifications && value?.certifications?.length > 0;
  const hasInsurances = value?.insurances && value?.insurances?.length > 0;

  const itemCount =
    (hasBackgroundCheck ? 1 : 0) +
    (hasCertifications ? 1 : 0) +
    (hasInsurances ? 1 : 0);

  const isSingleItem = itemCount === 1;

  return (
    <Box
      sx={{
        overflowX: "auto",
        scrollbarWidth: "none",
        display: "flex",
        flexDirection: "column",
        justifyContent: isSingleItem ? "center" : "flex-start",
        minHeight: isSingleItem ? "100%" : "auto",
      }}
    >
      {hasBackgroundCheck && (
        <Typography display={"flex"} alignItems={"center"} fontSize={14}>
          Background Check Completed-
          <Chip
            sx={{ ml: 0.5 }}
            size="small"
            color="primary"
            variant="outlined"
            label={value.backgroundCheckDone ? "True" : "False"}
          />
        </Typography>
      )}
      {hasCertifications && (
        <Typography
          display={"flex"}
          alignItems={"center"}
          fontSize={14}
          mt={0.25}
        >
          Certifications-
          {value?.certifications.map((certification: string) => (
            <Chip
              key={certification}
              sx={{ ml: 0.5 }}
              size="small"
              color="primary"
              variant="outlined"
              label={certification}
            />
          ))}
        </Typography>
      )}
      {hasInsurances && (
        <Typography
          display={"flex"}
          alignItems={"center"}
          fontSize={14}
          mt={0.25}
        >
          Insurances-
          {value?.insurances.map((insurance: string) => (
            <Chip
              key={insurance}
              sx={{ ml: 0.5 }}
              size="small"
              color="primary"
              variant="outlined"
              label={insurance}
            />
          ))}
        </Typography>
      )}
    </Box>
  );
};

export default function Forms() {
  const dispatch = useDispatch();

  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [filterParams, setFilterParams] = useState<any>({});
  const [category, setCategory] = useState<any[]>([]);
  const [type, setType] = useState<any[]>([]);

  const { allForms } = useSelector((state: any) => state.clients);
  const { insuranceTypes, inspectionServices, certificationTypes } =
    useSelector((state: any) => state.inspectionFirms);

  const rowCountRef = useRef(allForms?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (allForms?.totalCount !== undefined) {
      rowCountRef.current = allForms?.totalCount;
    }
    return rowCountRef.current;
  }, [allForms?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "displayName",
      headerName: "Form Name",
      width: 180,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="name"
          sortField="name"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "inspectionTypeCategory",
      headerName: "Category",
      width: 180,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="category"
          sortField="category"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
          values={category}
        />
      ),
    },
    {
      field: "inspectionType",
      headerName: "Type",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) => {
        const findType = inspectionServices.find(
          (t: any) => t?.inspectionType === value
        );
        return findType ? findType?.displayName : "-";
      },
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="type"
          sortField="type"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
          values={type}
        />
      ),
    },
    {
      field: "clientFee",
      headerName: "Client Fee",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) => (value ? `$${value}` : " "),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="clientFee"
          sortField="client_fee"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
    {
      field: "restrictions",
      headerName: "Restrictions",
      minWidth: 280,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <RestrictionsRenderer value={params.value} />
      ),
      renderHeader: (params) => (
        <TableHeaderMultiSelectField
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
          values={{
            insuranceTypes:
              Array.from(insuranceTypes, (obj: any) => obj.insuranceType) || [],
            certificateTypes:
              Array.from(
                certificationTypes,
                (obj: any) => obj.certificationType
              ) || [],
          }}
        />
      ),
    },
    {
      field: "updatedDateTime",
      headerName: "Last Updated",
      width: 200,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY, h:mma") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="lastUpdated"
          sortField="last_updated"
          headerName={params.colDef.headerName}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterForm}
        />
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });

    dispatch(getFormsSuccess({ response: null }));
    if (inspectionServices.length === 0)
      dispatch(getInspectionServicesRequest());
    if (insuranceTypes.length === 0) dispatch(getInsuranceTypesRequest());
    if (certificationTypes.length === 0)
      dispatch(getCertificationTypesRequest());
  }, []); // eslint-disable-line

  useEffect(() => {
      dispatch(
        getFormsRequest({ page: 1, pageSize: 10 })
      );
  }, [dispatch]);

  useEffect(() => {
    if (inspectionServices && inspectionServices.length > 0) {
      const uniqueCategories = inspectionServices.reduce(
        (acc: any[], item: any) => {
          if (!acc.includes(item.category)) {
            acc.push(item.category);
          }
          return acc;
        },
        []
      );
      setCategory(uniqueCategories);

      const inspectionType = inspectionServices.map((service: any) => ({
        value: service?.inspectionType,
        label: service?.displayName,
      }));
      setType(inspectionType);
    }
  }, [inspectionServices]);

  useEffect(() => {
    setRowData(allForms?.items);
  }, [allForms]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterForm = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else if (keys.includes("backgroundCheckCompleted")) {
      // handle form template restrictions filter
      if (data.backgroundCheckCompleted === false) {
        const { backgroundCheckCompleted, ...restrinctionFilter } = data;
        if (filterParams.hasOwnProperty("backgroundCheckCompleted")) {
          delete filterParams["backgroundCheckCompleted"];
        }
        setFilterParams({ ...filterParams, ...restrinctionFilter });
      } else {
        setFilterParams({ ...filterParams, ...data });
      }
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getFormsRequest({ ...filters }));
    }
  }, [filterParams]); // eslint-disable-line

  return (
    <>
      <Box
        className="clients-header"
        sx={{
          my: 1.5,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Box>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          rowData={rowData}
          rowCount={rowCount}
          columnDef={column}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          checkboxSelection={false}
          rowHeight={90}
          disableColumnResize={false}
        />
      </Box>
    </>
  );
}
